import { useCallback, useEffect, useState } from 'react';
// A wrapper for "JSON.parse()"" to support "undefined" value
var parseJSON = function (value) {
    try {
        return value === 'undefined' ? undefined : JSON.parse(value !== null && value !== void 0 ? value : '');
    }
    catch (_a) {
        // console.log('parsing error on', { value });
        return undefined;
    }
};
/**
 * A custom hook that uses sessionStorage to store and retrieve values.
 *
 * @param {string} key The key to use for the sessionStorage
 * @param {any} initialValue The initial value to use if the key is not found in sessionStorage
 *
 * @returns A tuple of the value and a setter function
 */
var useSessionStorage = function (key, initialValue) {
    // Get the item from sessionStorage
    var readValue = useCallback(function () {
        // Prevent build error "window is undefined" but keep keep working
        if (typeof window === 'undefined') {
            return initialValue;
        }
        // Get the value from sessionStorage or return the initialValue with a warning if not found
        try {
            var item = window.sessionStorage.getItem(key);
            return item ? parseJSON(item) : initialValue;
        }
        catch (error) {
            console.warn("Error reading sessionStorage key \u201C".concat(key, "\u201D:"), error);
            return initialValue;
        }
    }, [initialValue, key]);
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    var _a = useState(readValue), storedValue = _a[0], setStoredValue = _a[1];
    // Return a wrapped version of useState's setter function that will store the new value in sessionStorage.
    var setValue = useCallback(function (value) {
        // Prevent build error "window is undefined" but keeps working
        if (typeof window == 'undefined') {
            console.warn("Tried setting sessionStorage key \u201C".concat(key, "\u201D even though environment is not a client"));
        }
        try {
            // Allow value to be a function so we have the same API as useState
            var newValue = value instanceof Function ? value(storedValue) : value;
            // Save to session storage
            window.sessionStorage.setItem(key, JSON.stringify(newValue));
            // Save state
            setStoredValue(newValue);
        }
        catch (error) {
            console.warn("Error setting sessionStorage key \u201C".concat(key, "\u201D:"), error);
        }
    }, [key, storedValue]);
    // Set the value to the initial value the first time useSessionStorage is called
    useEffect(function () {
        setStoredValue(readValue());
    }, []);
    return [storedValue, setValue];
};
export { useSessionStorage };
